import Helper, { DateFormat } from "@/services/Helper";
import { WorkOrderModel } from "./WorkOrderModel";

class JobModel
{
    constructor(obj?: JobModel)
    {
        if (obj)
        {
            Object.assign(this, obj);

            // Store date strings in private backing fields for proper getter/setter handling
            if (obj.CreateDate)
            {
                this._CreateDate = obj.CreateDate + "";
            }

            if (obj.DueDate)
            {
                this._DueDate = obj.DueDate + "";
            }

            if (obj.CompletionDate)
            {
                this._CompletionDate = obj.CompletionDate + "";
            }

            if (obj.InvoicedOn)
            {
                this._InvoicedOn = obj.InvoicedOn + "";
            }

            if (obj.InvoicedOn2)
            {
                this._InvoicedOn2 = obj.InvoicedOn2 + "";
            }

            if (obj.PaidOn)
            {
                this._PaidOn = obj.PaidOn + "";
            }

            // Handle WorkOrders relationship
            if (obj.WorkOrders)
            {
                this.WorkOrders = obj.WorkOrders.map(wo => new WorkOrderModel(wo));
            }
        }
        else
        {
            // Set defaults for new records
            this._CreateDate = Helper.dateToInput(new Date(), DateFormat.Date);
            this.RequestStatus = 1;
        }
    }

    public Id?: number | null;
    
    private _CreateDate?: string | null;

    public get CreateDate (): string | null
    {
        return Helper.dateToInput(this.CreateDateAsDate, DateFormat.Date) || null;
    }

    public set CreateDate (val: string | null)
    {
        this._CreateDate = val || null;
    }

    public get CreateDateAsDate (): Date | null
    {
        if (this._CreateDate)
        {
            return Helper.dateFromString(this._CreateDate) || null;
        }

        return null;
    }
    
    public Customer?: number | null;
    
    public RequestDetailedDescription?: string | null;
    
    public TaskDetail?: string | null;
    
    public Priority?: number | null;
    
    private _DueDate?: string | null;

    public get DueDate (): string | null
    {
        return Helper.dateToInput(this.DueDateAsDate, DateFormat.Date) || null;
    }

    public set DueDate (val: string | null)
    {
        this._DueDate = val || null;
    }

    public get DueDateAsDate (): Date | null
    {
        if (this._DueDate)
        {
            return Helper.dateFromString(this._DueDate) || null;
        }

        return null;
    }
    
    public Location?: string | null;
    
    public Technician?: number | null;
    
    public RequestType?: string | null;
    
    public RequestStatus?: number | null;
    
    public WorkTime?: string | null;
    
    private _CompletionDate?: string | null;

    public get CompletionDate (): string | null
    {
        return Helper.dateToInput(this.CompletionDateAsDate, DateFormat.Date) || null;
    }

    public set CompletionDate (val: string | null)
    {
        this._CompletionDate = val || null;
    }

    public get CompletionDateAsDate (): Date | null
    {
        if (this._CompletionDate)
        {
            return Helper.dateFromString(this._CompletionDate) || null;
            
        }

        return null;
    }

    private _InvoicedOn?: string | null;

    public get InvoicedOn (): string | null
    {
        return Helper.dateToInput(this.InvoicedOnAsDate, DateFormat.Date) || null;
    }

    public set InvoicedOn (val: string | null)
    {
        this._InvoicedOn = val || null;
    }

    public get InvoicedOnAsDate (): Date | null
    {
        if (this._InvoicedOn)
        {
            return Helper.dateFromString(this._InvoicedOn) || null;
        }

        return null;
    }

    public InvoiceNumber?: string | null;
    
    private _PaidOn?: string | null;

    public get PaidOn (): string | null
    {
        return Helper.dateToInput(this.PaidOnAsDate, DateFormat.Date) || null;
    }

    public set PaidOn (val: string | null)
    {
        this._PaidOn = val || null;
    }

    public get PaidOnAsDate (): Date | null
    {
        if (this._PaidOn)
        {
            return Helper.dateFromString(this._PaidOn) || null;
            
        }

        return null;
    }

    public EstimatedAmount?: number | null;
    
    public EstimateNumber?: string | null;
    
    public TimeEstimate?: string | null;
    
    public AssignedPriority?: string | null;
    
    public BillingNotes?: string | null;
    
    public ProjectFolder?: string | null;
    
    public InvoiceDescription?: string | null;
    
    private _InvoicedOn2?: string | null;

    public get InvoicedOn2 (): string | null
    {
        return Helper.dateToInput(this.InvoicedOn2AsDate, DateFormat.Date) || null;
    }

    public set InvoicedOn2 (val: string | null)
    {
        this._InvoicedOn2 = val || null;
    }

    public get InvoicedOn2AsDate (): Date | null
    {
        if (this._InvoicedOn2)
        {
            return Helper.dateFromString(this._InvoicedOn2) || null;
        }

        return null;
    }
    
    public InvoiceNumber2?: string | null;
    
    public InvoiceDescription2?: string | null;
    
    public InvoiceTotal?: number | null;
    
    public ShouldBillTotal?: number | null;
    
    public Project?: boolean | null;
    
    public Requestor?: string | null;
    
    public ServiceReport?: string | null;
    
    public Progress?: number | null;
    
    public TextStatus?: string | null;

    public DepartmentCategory?: string | null;

    public Department?: string | null;

    public RecCount?: number | null;

    // WorkOrders collection for this request
    public WorkOrders?: WorkOrderModel[] | null;

    // Computed property to get comma-separated WorkOrder IDs with Title="Request"
    public get RequestWorkOrderIds(): string | null {
        if (!this.WorkOrders || this.WorkOrders.length === 0) {
            return null;
        }
        
        const requestWorkOrders = this.WorkOrders.filter(wo => wo.Title === "Request");
        if (requestWorkOrders.length === 0) {
            return null;
        }
        
        return requestWorkOrders
            .map(wo => wo.Id)
            .filter(id => id != null)
            .join(', ');
    }
}

class JobFilter
{
    IncludeContact?: boolean = false;
    
    IncludeOwner?: boolean = false;
    
    IncludeTasks?: boolean = false;
    
    IncludeParts?: boolean = false;
    
    IncludeExpenses?: boolean = false;
    
    IncludeMileage?: boolean = false;
    
    IncludePMs?: boolean = false; // By default, exclude PM-linked requests from job lists
    
    IncludeWorkOrders?: boolean = false;
    
    Id?: Array<Number> | null = null;

    Company?: Array<Number> | null = null;
    
    Status?: Array<Number> | null = null;
    
    Owner?: Array<Number> | null = null;

    Department?: string[] | null = null;
    
    Page?: Number = 0;
    
    Size?: Number = 25;
}

class RequestFilter extends JobFilter
{

}

class RequestModel extends JobModel
{
    // ... alias
}


export { JobModel, RequestModel, JobFilter, RequestFilter }